import { t } from "@lingui/macro";
import {
  CheckCircle,
  CircleNotch,
  Lightning,
  Target,
  WarningCircle,
} from "@phosphor-icons/react";
import { Button, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useState } from "react";

import { useFeatureGate } from "@/client/hooks/use-feature-gate";
import { useTakingLonger } from "@/client/hooks/use-taking-longer";
import { useToast } from "@/client/hooks/use-toast";
import { askAssistantToApply } from "@/client/libs/ai-assist";
import { processAi, reportAiOutcome } from "@/client/services/ai";
import { useResumeStore } from "@/client/stores/resume";

type ScoreResult = {
  score: number;
  suggestions: string[];
  summary: string;
};

const ScoreGauge = ({ score }: { score: number }) => {
  const color =
    score >= 80 ? "text-green-500" : score >= 60 ? "text-yellow-500" : "text-red-500";
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative mx-auto h-36 w-36">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-border/30"
        />
        <motion.circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className={cn(color)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className={cn("text-3xl font-bold tabular-nums", color)}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          {score}
        </motion.span>
        <span className="text-[10px] text-foreground/40">{t`ATS Score`}</span>
      </div>
    </div>
  );
};

export const ATSScoreSection = () => {
  const { toast } = useToast();
  const { gate } = useFeatureGate();
  const [loading, setLoading] = useState(false);
  const takingLonger = useTakingLonger(loading);
  const [result, setResult] = useState<ScoreResult | null>(null);

  const handleCheck = useCallback(async () => {
    if (!gate("ats-score")) return;

    const { resume } = useResumeStore.getState();

    setLoading(true);
    try {
      const response = await processAi({
        action: "review-resume",
        context: { resumeData: resume.data },
      });

      if (response.ok === false) {
        toast({
          variant: "error",
          title: t`Please try again`,
          description: response.result,
        });
        return;
      }

      setResult({
        score: typeof response.score === "number" ? response.score : 0,
        summary: response.result,
        suggestions: Array.isArray(response.suggestions) ? response.suggestions : [],
      });
      reportAiOutcome(response.callId, "used");
    } catch {
      toast({
        variant: "error",
        title: t`Failed to check ATS score`,
        description: t`Please try again.`,
      });
    } finally {
      setLoading(false);
    }
  }, [gate, toast]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t`ATS Score`}
        </Label>
        <p className="text-[11px] text-muted-foreground">
          {t`Check how your resume scores against ATS systems used by 85%+ of companies.`}
        </p>
      </div>

      {!result && (
        <Button
          onClick={handleCheck}
          disabled={loading}
          className="w-full"
          size="sm"
        >
          {loading ? (
            <>
              <CircleNotch size={14} className="mr-1.5 animate-spin" />
              {takingLonger ? t`Taking longer than usual…` : t`Analyzing...`}
            </>
          ) : (
            <>
              <Target size={14} className="mr-1.5" weight="bold" />
              {t`Check ATS Score`}
            </>
          )}
        </Button>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <ScoreGauge score={result.score} />

            {result.summary && (
              <div className="rounded-lg border border-border/30 bg-secondary-accent/20 p-3">
                <div className="flex items-start gap-2">
                  <Lightning size={12} weight="fill" className="mt-0.5 shrink-0 text-primary" />
                  <p className="text-[11px] leading-relaxed text-foreground/60">
                    {result.summary}
                  </p>
                </div>
              </div>
            )}

            {result.suggestions.length > 0 && (
              <div className="space-y-2">
                {result.suggestions.map((suggestion, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * i }}
                    className="flex items-start gap-2 rounded-md border border-border/20 px-2.5 py-2"
                  >
                    {i < 2 ? (
                      <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-green-500" />
                    ) : (
                      <WarningCircle
                        size={14}
                        weight="fill"
                        className="mt-0.5 shrink-0 text-yellow-500"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] leading-relaxed text-foreground/70">{suggestion}</p>
                      <button
                        type="button"
                        className="mt-1 text-[10px] font-medium text-primary hover:underline"
                        onClick={() => askAssistantToApply(suggestion)}
                      >
                        {t`Fix this in chat`}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={loading}
              onClick={() => { setResult(null); handleCheck(); }}
            >
              {loading ? (
                <>
                  <CircleNotch size={14} className="mr-1.5 animate-spin" />
                  {takingLonger ? t`Taking longer than usual…` : t`Re-checking...`}
                </>
              ) : (
                t`Re-check`
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
